import { customElement, resolve } from 'aurelia';
import { I18N } from '@aurelia/i18n';
import template from './forms-list.html';
import {
  createForm,
  deleteForm,
  listForms,
  updateForm,
  createImportProtocol,
  completeImportProtocol,
} from '../services/api.service';
import type { Document, FormDefinition, FormFieldType } from '../services/api.types';
import { ConfirmDialog } from '../components/confirm-dialog';

type ExportedFormField = {
  name: string;
  dataType: FormFieldType;
  tagGroupId: string | null;
  addTagToDocument: boolean;
  uniqueForDocument: boolean;
};

type ExportedForm = {
  title: string;
  isActive: boolean;
  fields: ExportedFormField[];
};

interface FormExport {
  format: 'radishdms-forms';
  version: 1;
  exportedAt: string;
  forms: ExportedForm[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

@customElement({ name: 'forms-list', template })
export class FormsList {
  private readonly i18n: I18N = resolve(I18N);

  public forms: FormDefinition[] = [];
  public activeTab: 'forms' | 'views' = 'forms';
  public errorMessage = '';
  public importing = false;
  public showModal = false;
  public formToEdit: FormDefinition | null = null;
  public importInput: HTMLInputElement | null = null;
  public documentToView: Document | null = null;
  public editDocument: Document | null = null;
  public initialFilesForEditModal: File[] = [];
  public pendingImportProtocolNumber: number | null = null;
  public formViewDocumentOpen = false;

  public async loading(): Promise<void> {
    await this.fetchForms();
  }

  public async fetchForms(): Promise<void> {
    this.errorMessage = '';
    try {
      this.forms = await listForms();
    } catch (error: unknown) {
      this.errorMessage = error instanceof Error
        ? error.message
        : this.i18n.tr('forms.loadFailed');
    }
  }

  public exportForm(form: FormDefinition): void {
    const exportData: FormExport = {
      format: 'radishdms-forms',
      version: 1,
      exportedAt: new Date().toISOString(),
      forms: [{
        title: form.title,
        isActive: form.isActive,
        fields: form.fields
          .slice()
          .sort((a, b) => a.position - b.position)
          .map(field => ({
            name: field.name,
            dataType: field.dataType,
            tagGroupId: field.tagGroupId ?? null,
            addTagToDocument: field.addTagToDocument,
            uniqueForDocument: field.uniqueForDocument,
          })),
      }],
    };

    this.downloadExport(
      exportData,
      `radishdms-form-${this.safeFilePart(form.title)}.json`,
    );
  }

  public openImportPicker(): void {
    this.importInput?.click();
  }

  public async onImportFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.importing) return;

    this.errorMessage = '';
    this.importing = true;

    try {
      const importedForms = this.parseFormExport(await file.text());
      const existingForms = new Map(
        this.forms.map(form => [this.normalizeTitle(form.title), form]),
      );
      const failures: string[] = [];

      for (const importedForm of importedForms) {
        const titleKey = this.normalizeTitle(importedForm.title);
        const existingForm = existingForms.get(titleKey);
        if (existingForm) {
          const confirmed = await ConfirmDialog.confirm(
            this.i18n.tr('forms.import.overwrite', { title: importedForm.title }),
            this.i18n.tr('forms.import.overwriteTitle'),
          );
          if (!confirmed) continue;
        }

        const dto = {
          title: importedForm.title,
          isActive: importedForm.isActive,
          fields: importedForm.fields.map(field => ({
            name: field.name,
            dataType: field.dataType,
            tagGroupId: field.tagGroupId,
            addTagToDocument: field.addTagToDocument,
            uniqueForDocument: field.uniqueForDocument,
          })),
        };

        try {
          const saved = existingForm
            ? await updateForm(existingForm.id, dto)
            : await createForm(dto);
          existingForms.set(titleKey, saved);
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : String(error);
          failures.push(`${importedForm.title}: ${message}`);
        }
      }

      await this.fetchForms();
      if (failures.length > 0) {
        await ConfirmDialog.alert(
          `${this.i18n.tr('forms.import.failed', { count: failures.length })} ${failures.join(' ')}`,
          this.i18n.tr('common.error'),
        );
      }
    } catch (error: unknown) {
      await ConfirmDialog.alert(
        error instanceof Error
          ? error.message
          : this.i18n.tr('forms.import.invalidFile'),
        this.i18n.tr('common.error'),
      );
    } finally {
      this.importing = false;
    }
  }

  private parseFormExport(text: string): ExportedForm[] {
    let parsed: unknown;
    try {
      parsed = JSON.parse(text) as unknown;
    } catch {
      throw new Error(this.i18n.tr('forms.import.invalidFile'));
    }

    const entries = Array.isArray(parsed)
      ? parsed
      : isRecord(parsed) && Array.isArray(parsed.forms)
        ? parsed.forms
        : null;
    if (!entries) throw new Error(this.i18n.tr('forms.import.invalidFile'));
    if (entries.length === 0) throw new Error(this.i18n.tr('forms.import.empty'));

    return entries.map((entry, index) => {
      if (!isRecord(entry) ||
          typeof entry.title !== 'string' ||
          !entry.title.trim() ||
          !Array.isArray(entry.fields) ||
          entry.fields.length === 0) {
        throw new Error(this.i18n.tr('forms.import.invalidForm', { index: index + 1 }));
      }

      const fields = entry.fields.map((field, fieldIndex) => {
        if (!isRecord(field) ||
            typeof field.name !== 'string' ||
            !field.name.trim() ||
            !this.isFormFieldType(field.dataType)) {
          throw new Error(this.i18n.tr('forms.import.invalidField', {
            form: entry.title,
            index: fieldIndex + 1,
          }));
        }

        if (field.tagGroupId !== undefined &&
            field.tagGroupId !== null &&
            typeof field.tagGroupId !== 'string') {
          throw new Error(this.i18n.tr('forms.import.invalidField', {
            form: entry.title,
            index: fieldIndex + 1,
          }));
        }

        return {
          name: field.name.trim(),
          dataType: field.dataType,
          tagGroupId: typeof field.tagGroupId === 'string' && field.tagGroupId.trim()
            ? field.tagGroupId
            : null,
          addTagToDocument: field.addTagToDocument === true,
          uniqueForDocument: field.uniqueForDocument === true,
        };
      });

      return {
        title: entry.title.trim(),
        isActive: entry.isActive !== false,
        fields,
      };
    });
  }

  private isFormFieldType(value: unknown): value is FormFieldType {
    return value === 'text' ||
      value === 'number' ||
      value === 'date' ||
      value === 'boolean' ||
      value === 'tagSelect';
  }

  private downloadExport(exportData: FormExport, fileName: string): void {
    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    try {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  private safeFilePart(value: string): string {
    return value
      .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '_')
      .trim()
      .slice(0, 80) || 'form';
  }

  private normalizeTitle(title: string): string {
    return title.trim().toLocaleLowerCase();
  }

  public openCreateModal(): void {
    this.formToEdit = null;
    this.showModal = true;
  }

  public openEditModal(form: FormDefinition): void {
    this.formToEdit = form;
    this.showModal = true;
  }

  public onModalSaved(form: FormDefinition): void {
    const index = this.forms.findIndex(existing => existing.id === form.id);
    this.forms = index >= 0
      ? this.forms.map(existing => existing.id === form.id ? form : existing)
      : [...this.forms, form];
    this.closeModal();
  }

  public onModalClosed(): void {
    this.closeModal();
  }

  public onFormViewDocumentOpened(document: Document): void {
    this.documentToView = document;
    this.formViewDocumentOpen = true;
  }

  public onFormViewDocumentUpdated(document: Document): void {
    this.documentToView = document;
    this.formViewDocumentOpen = true;
  }

  public onFormViewDocumentClosed(): void {
    this.documentToView = null;
    this.formViewDocumentOpen = false;
  }

  public async onFormViewEditRequested(document: Document, files?: File[]): Promise<void> {
    if (files?.length) {
      try {
        const protocol = await createImportProtocol();
        if (!Number.isSafeInteger(protocol.number) || protocol.number <= 0) {
          throw new Error(this.i18n.tr('importProtocols.invalidNumber'));
        }
        this.pendingImportProtocolNumber = protocol.number;
      } catch (error: unknown) {
        await ConfirmDialog.alert(
          error instanceof Error ? error.message : this.i18n.tr('documents.dropZone.createError'),
          this.i18n.tr('common.error'),
        );
        return;
      }
      this.initialFilesForEditModal = [...files];
    }
    this.documentToView = null;
    this.editDocument = document;
    this.formViewDocumentOpen = true;
  }

  public async onFormDocumentModalClosed(): Promise<void> {
    await this.finishPendingImportProtocol();
    this.editDocument = null;
    this.formViewDocumentOpen = false;
    this.initialFilesForEditModal = [];
  }

  public async onFormDocumentSaved(): Promise<void> {
    await this.finishPendingImportProtocol();
    this.editDocument = null;
    this.formViewDocumentOpen = false;
    this.initialFilesForEditModal = [];
  }

  public async onFormDocumentDeleted(): Promise<void> {
    await this.finishPendingImportProtocol();
    this.editDocument = null;
    this.documentToView = null;
    this.formViewDocumentOpen = false;
    this.initialFilesForEditModal = [];
  }

  private async finishPendingImportProtocol(): Promise<void> {
    const number = this.pendingImportProtocolNumber;
    this.pendingImportProtocolNumber = null;
    if (number === null) return;
    try {
      await completeImportProtocol(number);
    } catch (error) {
      console.error('[Radish DMS] Could not complete import protocol', {
        protocolNumber: number,
        error,
      });
    }
  }

  public async removeForm(form: FormDefinition): Promise<void> {
    const confirmed = await ConfirmDialog.confirm(
      this.i18n.tr('forms.delete.confirm', { title: form.title }),
      this.i18n.tr('forms.delete.confirmTitle'),
    );
    if (!confirmed) return;

    try {
      await deleteForm(form.id);
      this.forms = this.forms.filter(existing => existing.id !== form.id);
    } catch (error: unknown) {
      this.errorMessage = error instanceof Error ? error.message : String(error);
    }
  }

  private closeModal(): void {
    this.showModal = false;
    this.formToEdit = null;
  }
}
